// The one E.164 definition for the whole app.
//
// Before this module there were three copies with two different shapes:
// features/auth/schemas.ts allowed 2-15 digits (so "+12" passed signup), while
// features/public and features/institute required 7-15. Signup was the loosest
// gate on the least-trusted input. Mirrored server-side in the
// complete_profile() regex (migration 202608091000) and in request-institute.
export const E164_RE = /^\+[1-9]\d{6,14}$/;

/** Human-readable example used in placeholders and error copy. */
export const E164_EXAMPLE = '+9779812345678';

export function isE164(value: string): boolean {
  return E164_RE.test(value.trim());
}

/**
 * Reduce a typed national number to bare digits.
 *
 * Users paste numbers with spaces, dashes and brackets, and Nepali numbers are
 * routinely written with a trunk "0" (`098…`) that must not survive into E.164.
 * The old inputs rejected all of that instead of cleaning it.
 */
export function normalizeNationalNumber(input: string): string {
  const digits = input.replace(/\D/g, '');
  return digits.replace(/^0+/, '');
}

/** Join a dial code and a national number into an E.164 string. */
export function composeE164(dial: string, national: string): string {
  const cleanDial = dial.startsWith('+') ? dial : `+${dial}`;
  const cleanNational = normalizeNationalNumber(national);
  if (cleanNational === '') return '';
  return `${cleanDial}${cleanNational}`;
}

export interface SplitPhone {
  dial: string;
  national: string;
}

/**
 * Split a stored E.164 value back into (dial code, national number) so an
 * existing number can be edited in the two-part field.
 *
 * `knownDials` must be ordered longest-first by the caller so that a "+977"
 * match is preferred over a "+9" one. Returns null when nothing matches, which
 * the caller treats as "fall back to the default dial code".
 */
export function splitE164(value: string, knownDials: readonly string[]): SplitPhone | null {
  const trimmed = value.trim();
  if (!trimmed.startsWith('+')) return null;
  const match = knownDials.find((dial) => trimmed.startsWith(dial) && trimmed.length > dial.length);
  if (match === undefined) return null;
  return { dial: match, national: trimmed.slice(match.length) };
}
